import React, { useState, useEffect } from 'react';
import { Search, Eye, FileText, Calendar, Building, TrendingUp, Package, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Send, Loader, Zap, CheckCircle } from 'lucide-react';
import Swal from "sweetalert2";
import API_BASE_URL from "../config";
import ACCOUNTING_API_BASE from '../AccountingAPI';
import POApprovalModal from '../Modal/POApprovalModal';

const POApproval = () => {
  const [poHeaders, setPoHeaders] = useState([]);
  const [vendors, setVendors] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPO, setSelectedPO] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [postingId, setPostingId] = useState(null);
  const [postingMode, setPostingMode] = useState('manual');

  // ✅ NEW: Set of poNumbers already posted to accounting
  const [postedNumbers, setPostedNumbers] = useState(new Set());

  const [sortConfig, setSortConfig] = useState({ key: 'poCreatedDate', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchPOHeaders();
    fetchVendors();
    fetchPostingMode();
    fetchPostedNumbers(); // ✅ NEW
  }, []);

  const fetchPOHeaders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PO_Header`);
      const data = await res.json();
      setPoHeaders(data);
    } catch (error) {
      console.error('Error fetching PO headers:', error);
      alert('Error loading PO list');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Vendor`);
      const data = await res.json();
      const vendorMap = {};
      data.forEach(v => {
        // Normalize: store by both possible id casings
        const id = v.id ?? v.Id ?? v.ID;
        if (id !== undefined && id !== null) {
          // Also normalize the vendor object keys we care about
          v._vendorName = v.VendorName || v.vendorName || v.vendor_name || "";
          v._paymentTerms = v.PaymentTerms || v.paymentTerms || v.payment_terms || "";
          vendorMap[String(id)] = v;
        }
      });
      setVendors(vendorMap);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchPostingMode = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/AccountingSystemSettings`);
      if (res.ok) {
        const data = await res.json();
        setPostingMode(data.postingMode || 'manual');
      }
    } catch (error) {
      console.error('Error fetching posting mode:', error);
      setPostingMode('manual');
    }
  };

  // ✅ NEW: Fetch all POs already in accounting, build a Set of poNumbers
  const fetchPostedNumbers = async () => {
    try {
      const res = await fetch(`${ACCOUNTING_API_BASE}/PurchaseOrder`);
      if (!res.ok) return;
      const data = await res.json();
      const numbers = new Set(
        (Array.isArray(data) ? data : []).map(p => p.poNumber).filter(Boolean)
      );
      setPostedNumbers(numbers);
    } catch (error) {
      console.error('Error fetching posted PO numbers:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
      1: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      2: { label: 'Approved', color: 'bg-green-100 text-green-800' },
      3: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
      4: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800' }
    };
    const statusInfo = statusMap[status] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (sortConfig.key === 'poCreatedDate' || sortConfig.key === 'deliveryDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleViewPO = (po) => {
    setSelectedPO(po);
    setIsModalOpen(true);
  };

  const handlePostToAccounting = async (po, silent = false) => {
    if (!silent) {
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Post to Accounting System",
        html: `Post <strong>${po.poNumber}</strong> to the accounting system?`,
        showCancelButton: true,
        confirmButtonText: "Yes, Post Now",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#7c3aed",
      });
      if (!isConfirmed) return;
    }

    setPostingId(po.id);
    try {
      const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${po.id}`);
      const detailsData = await detailsRes.json();
      const poDetails = Array.isArray(detailsData) ? detailsData : [detailsData];

      // ✅ Normalize vendorId key — handle int vs string mismatch
      const vendorData = vendors[String(po.vendorId)] || vendors[po.vendorId] || {};
      const resolvedVendorName = vendorData._vendorName || vendorData.VendorName || vendorData.vendorName || "";
      const resolvedPaymentTerms = vendorData._paymentTerms || vendorData.PaymentTerms || vendorData.paymentTerms || po.paymentTerms || po.terms || "";

      const calculateLineTotal = (qty, unitPrice, discount) => {
        const subtotal = qty * unitPrice;
        return subtotal - (subtotal * (discount || 0)) / 100;
      };

      const subtotal = poDetails.reduce(
        (sum, item) => sum + calculateLineTotal(item.qty, item.unitPrice, item.detailDiscount), 0
      );
      const totalDiscount = poDetails.reduce((sum, item) => {
        const lineTotal = item.qty * item.unitPrice;
        return sum + (lineTotal * (item.detailDiscount || 0)) / 100;
      }, 0);
      const taxAmount = (!po.isNonVAT && po.isVatInclusive) ? subtotal * 0.12 : 0;
      const headerDiscountAmount = (subtotal * (po.headerDiscount || 0)) / 100;
      const grandTotal = subtotal - headerDiscountAmount + taxAmount;

      const purchaseOrderDetails = poDetails.map((item) => {
        const lineSubtotal = item.qty * item.unitPrice;
        const lineDiscount = (lineSubtotal * (item.detailDiscount || 0)) / 100;
        const lineTotal = lineSubtotal - lineDiscount;
        return {
          purchaseOrderHeaderId: 0,
          itemCode: item.itemCode || item.itemDescription || "",
          quantity: item.qty,
          taxationCodeId: 0,
          unitPrice: item.unitPrice,
          subTotal: lineSubtotal,
          totalDiscount: lineDiscount,
          taxAmount: 0,
          totalAmount: lineTotal,
          isReceived: false,
          notes: item.specification || "",
        };
      });

      const payload = {
        purchaseOrderDetails,
        poNumber: po.poNumber,
        vendorId: String(po.vendorId),
        vendorVendorId: String(po.vendorId),
        vendorName1: resolvedVendorName,
        vendorName2: null,
        poDate: po.poCreatedDate || new Date().toISOString(),
        paymentTerms: resolvedPaymentTerms,
        currency: "PHP",
        status: "Open",
        subTotal: subtotal,
        totalDiscount: totalDiscount,
        taxAmount: taxAmount,
        totalAmount: grandTotal,
        reference: po.referencePRId ? String(po.referencePRId) : "",
        notes: po.remarks || "",
        createdDate: po.poCreatedDate || new Date().toISOString(),
        createdBy: 0,
      };

      const res = await fetch(`${ACCOUNTING_API_BASE}/PurchaseOrder/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify([payload]),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Accounting API error ${res.status}: ${errText}`);
      }

      // ✅ Add to local postedNumbers set immediately (no need to re-fetch)
      setPostedNumbers(prev => new Set([...prev, po.poNumber]));

      // Log
      const userSession = localStorage.getItem("user");
      if (userSession) {
        const sessionData = JSON.parse(userSession);
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "POST-ACCOUNTING",
            module: "Purchase Order",
            description: `Posted PO #${po.poNumber} to accounting system. Mode: ${silent ? 'auto' : 'manual'}`,
            userId: sessionData.id || sessionData.userId,
            username: sessionData.username,
            fullName: `${sessionData.firstName ?? ""} ${sessionData.lastName ?? ""}`.trim() || sessionData.username,
          }),
        }).catch((err) => console.error("Log failed:", err));
      }

      if (!silent) {
        await Swal.fire({
          icon: "success",
          title: "Posted Successfully!",
          html: `<strong>${po.poNumber}</strong> has been posted to the accounting system.`,
          confirmButtonText: "OK",
          confirmButtonColor: "#7c3aed",
        });
      }

    } catch (error) {
      console.error("Error posting to accounting:", error);
      await Swal.fire({
        icon: "error",
        title: "Post Failed",
        html: `<strong>${po.poNumber}</strong> failed to post.<br/><small>${error.message}</small>`,
      });
    } finally {
      setPostingId(null);
    }
  };

  const handleApproveCallback = async (po, shouldAutoPost = false) => {

    await fetchPOHeaders()
    setIsModalOpen(false);
    if (shouldAutoPost) {
      const updatedPo = { ...po, status: 2 };
      await handlePostToAccounting(updatedPo, true);
    }
  };

  const filteredPOs = sortData(poHeaders.filter(po => {
    const v = vendors[String(po.vendorId)] || vendors[po.vendorId] || {};
    const vendorName = (v._vendorName || v.VendorName || v.vendorName || '').toLowerCase();
    const matchesSearch =
      po.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.shipTo_Location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendorName.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || po.status.toString() === statusFilter;
    return matchesSearch && matchesStatus;
  }));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPOs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPOs.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push('...'); pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1); pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1); pages.push('...');
      pages.push(currentPage - 1); pages.push(currentPage); pages.push(currentPage + 1);
      pages.push('...'); pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="min-h-screen from-blue-50 via-slate-50 to-blue-100 p-3 sm:p-6">
      <div className="max-w-8xl mx-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 shadow-lg mb-4 sm:mb-6">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2 sm:gap-3">
                <Package className="w-5 h-5 sm:w-7 sm:h-7 shrink-0" />
                Purchase Order Approval
              </h1>
              <p className="text-blue-100 text-xs sm:text-sm mt-1">Review and approve purchase orders</p>
            </div>
            <div className="flex items-center gap-2">
              <div className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${postingMode === 'auto'
                ? 'bg-green-500/30 text-green-100'
                : 'bg-white/20 text-white/80'
                }`}>
                <Zap className="w-3.5 h-3.5" />
                {postingMode === 'auto' ? 'Auto-post ON' : 'Manual post'}
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-2 rounded-lg shrink-0">
                <p className="text-xs text-white/80 font-semibold uppercase">Total POs</p>
                <p className="text-xl sm:text-2xl font-bold text-white text-center">{poHeaders.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl border border-white/20 shadow-lg px-3 sm:px-4 py-2.5 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3 flex-wrap">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Search PO, Vendor, Location..."
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm placeholder:text-gray-400"
          />
          <div className="w-px h-5 bg-gray-200 shrink-0" />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="bg-transparent border-none outline-none text-sm text-gray-700 cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="0">Draft</option>
            <option value="1">Pending</option>
            <option value="2">Approved</option>
            <option value="3">Rejected</option>
            <option value="4">Cancelled</option>
          </select>
          <div className="w-px h-5 bg-gray-200 shrink-0" />
          <select
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="bg-transparent border-none outline-none text-sm text-gray-700 cursor-pointer"
          >
            <option value="5">5 / page</option>
            <option value="10">10 / page</option>
            <option value="25">25 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
          </select>
          {statusFilter !== 'all' && (
            <>
              <div className="w-px h-5 bg-gray-200 shrink-0" />
              <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">
                {['Draft', 'Pending', 'Approved', 'Rejected', 'Cancelled'][Number(statusFilter)]}
                <button onClick={() => setStatusFilter('all')} className="hover:text-blue-900 leading-none">×</button>
              </span>
            </>
          )}
        </div>

        {/* Table */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 bg-gray-50/50 border-b border-gray-200">
            <p className="text-xs sm:text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredPOs.length === 0 ? 0 : indexOfFirstItem + 1}</span>–
              <span className="font-semibold text-gray-900">{Math.min(indexOfLastItem, filteredPOs.length)}</span> of{' '}
              <span className="font-semibold text-gray-900">{filteredPOs.length}</span> results
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
              <p className="text-gray-500 mt-4 text-sm font-medium">Loading purchase orders...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      {[
                        { label: 'PO Number', key: 'poNumber' },
                        { label: 'Status', key: 'status' },
                        { label: 'Vendor', key: 'vendorId' },
                        { label: 'Ship To', key: 'shipTo_Location' },
                        { label: 'Created', key: 'poCreatedDate' },
                        { label: 'Delivery', key: 'deliveryDate' },
                      ].map(({ label, key }) => (
                        <th
                          key={key}
                          className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                          onClick={() => handleSort(key)}
                        >
                          <div className="flex items-center gap-1">
                            {label}
                            {getSortIcon(key)}
                          </div>
                        </th>
                      ))}
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-semibold uppercase whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentItems.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-lg">No purchase orders found</p>
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((po) => {
                        const isApproved = po.status === 2 || po.status === '2';
                        const isBeingPosted = postingId === po.id;
                        // ✅ Check if this PO's poNumber already exists in accounting
                        const isAlreadyPosted = postedNumbers.has(po.poNumber);

                        return (
                          <tr key={po.id} className="hover:bg-blue-50/50 transition-colors">
                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                                <span className="font-semibold text-gray-900 text-sm">{po.poNumber}</span>
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              {getStatusBadge(po.status)}
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-gray-400 shrink-0" />
                                <span className="text-sm text-gray-700 font-medium max-w-[140px] truncate">
                                  {vendors[String(po.vendorId)]?._vendorName || vendors[String(po.vendorId)]?.VendorName || vendors[String(po.vendorId)]?.vendorName || `Vendor ${po.vendorId}`}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="text-sm text-gray-900 font-medium max-w-[140px] truncate">{po.shipTo_Location}</div>
                              <div className="text-xs text-gray-500 max-w-[140px] truncate">{po.shipTo_Address1}</div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                                <span className="text-sm text-gray-700">{formatDate(po.poCreatedDate)}</span>
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-gray-400 shrink-0" />
                                <span className="text-sm text-gray-700">{formatDate(po.deliveryDate)}</span>
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="flex items-center justify-center gap-2 flex-nowrap">
                                {/* View button — always visible */}
                                <button
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-all shadow-md whitespace-nowrap"
                                  onClick={() => handleViewPO(po)}
                                >
                                  <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                  View
                                </button>

                                {postingMode === 'manual' && (
                                  isAlreadyPosted ? (
                                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-500 text-xs font-semibold rounded-lg whitespace-nowrap border border-gray-200 cursor-not-allowed">
                                      <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                      Posted
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => isApproved && handlePostToAccounting(po)}
                                      disabled={!isApproved || isBeingPosted}
                                      title={!isApproved ? 'Approve this PO first before posting' : 'Post to accounting'}
                                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-white text-xs sm:text-sm font-medium rounded-lg transition-all shadow-md whitespace-nowrap ${!isApproved || isBeingPosted
                                          ? 'bg-gray-300 cursor-not-allowed opacity-60'
                                          : 'bg-violet-600 hover:bg-violet-700'
                                        }`}
                                    >
                                      {isBeingPosted ? (
                                        <><Loader className="w-3.5 h-3.5 animate-spin" />Posting...</>
                                      ) : (
                                        <><Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />Post</>
                                      )}
                                    </button>
                                  )
                                )}

                                {postingMode === 'auto' && isApproved && (
                                  isAlreadyPosted ? (
                                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-500 text-xs font-semibold rounded-lg whitespace-nowrap border border-gray-200 cursor-not-allowed">
                                      <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                      Posted
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-lg whitespace-nowrap">
                                      <Zap className="w-3 h-3" />
                                      Auto-post
                                    </span>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredPOs.length > 0 && (
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50/50 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-xs sm:text-sm text-gray-600">
                      Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
                      <span className="font-semibold text-gray-900">{totalPages}</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <button onClick={() => paginate(1)} disabled={currentPage === 1}
                        className="p-1.5 sm:p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        <ChevronsLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                      <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}
                        className="p-1.5 sm:p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => (
                          page === '...' ? (
                            <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-500 text-sm">...</span>
                          ) : (
                            <button key={page} onClick={() => paginate(page)}
                              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${currentPage === page
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}>
                              {page}
                            </button>
                          )
                        ))}
                      </div>
                      <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}
                        className="p-1.5 sm:p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                      <button onClick={() => paginate(totalPages)} disabled={currentPage === totalPages}
                        className="p-1.5 sm:p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        <ChevronsRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isModalOpen && selectedPO && (
        <POApprovalModal
          po={selectedPO}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onApprove={handleApproveCallback}
          onReject={() => { fetchPOHeaders(); setIsModalOpen(false); }}
          getStatusBadge={getStatusBadge}
          formatDate={formatDate}
          postingMode={postingMode}
        />
      )}
    </div>
  );
};

export default POApproval;